/*  Shared  */
import { Client, Device } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  React  */
import { TFunction } from 'i18next';
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';

/* Components */
import { TopInfoBanner } from './TopInfoBanner';

export enum ShowPartnerTopInfoBannerTypes {
    GET_STARTED = 'getStarted',
    SHOW_ME_HOW_TO_DEPLOY = 'showMeHowToDeploy'
}

const getBannerData = (t: TFunction<'translation', undefined>) => ({
    [ShowPartnerTopInfoBannerTypes.GET_STARTED]: {
        infoText: t('Banner.getStarted.text')
    },
    [ShowPartnerTopInfoBannerTypes.SHOW_ME_HOW_TO_DEPLOY]: {
        infoText: t('Banner.showMeHowToDeploy.text')
    }
});

const unload = ({
    clientInstance,
    deviceInstance
}: {
    clientInstance: Client;
    deviceInstance: Device;
}) => {
    clientInstance.cancel();
    deviceInstance.cancel();
};

const load = async ({
    clientInstance,
    deviceInstance,
    setClientsCount,
    setDeviceCount,
    setLoaded
}: {
    clientInstance: Client;
    deviceInstance: Device;
    setClientsCount: Dispatch<SetStateAction<number>>;
    setDeviceCount: Dispatch<SetStateAction<number>>;
    setLoaded: Dispatch<SetStateAction<boolean>>;
}) => {
    try {
        const clientsCount = await clientInstance.getClientCount({});
        setClientsCount(clientsCount || 0);
    } catch (err) {
        debug.e('Could not load clients count', err);
    }
    try {
        const deviceCount = await deviceInstance.getNumberOfDevicesCount({});
        setDeviceCount(deviceCount || 0);
    } catch (err) {
        debug.e('Could not load device count', err);
    } finally {
        setLoaded(true);
    }
};

export const ShowPartnerTopInfoBanner = ({
    type
}: {
    type: ShowPartnerTopInfoBannerTypes;
}) => {
    const { t } = useTranslation();
    const [clientsCount, setClientsCount] = useState(0); // under partner
    const [deviceCount, setDeviceCount] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const clientInstance = useMemo(() => new Client(), []);
    const deviceInstance = useMemo(() => new Device(), []);

    useEffect(() => {
        load({
            clientInstance,
            deviceInstance,
            setClientsCount,
            setDeviceCount,
            setLoaded
        });
    }, [clientInstance, deviceInstance]);

    // This is componentWillUnmount
    useEffect(
        () => () => {
            unload({ clientInstance, deviceInstance });
        },
        [clientInstance, deviceInstance]
    );

    // Currently type can be "getStarted", or "showMeHowToDeploy"
    if (
        type !== ShowPartnerTopInfoBannerTypes.GET_STARTED &&
        type !== ShowPartnerTopInfoBannerTypes.SHOW_ME_HOW_TO_DEPLOY
    ) {
        return null;
    }

    const data = getBannerData(t);

    const showBanner = !clientsCount || !deviceCount;

    return (
        loaded &&
        showBanner && (
            /* @ts-expect-error - File needs to be typed */
            <TopInfoBanner infoText={data[type].infoText} displayIcon={false} />
        )
    );
};
